import React, { useState } from 'react';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { Trans } from '@lingui/react/macro';
import { ProductFileUpload, ProjectCatalogProduct, OptionalId, ProductType } from '@/types';
import { Banner, LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import { EditProjectCatalogProductWizard } from '../products/edit-product-wizard/EditProductWizard';
import { getProjectCatalogProduct, saveProjectCatalogProduct } from '@/services/project-catalog';
import { useGetActiveProfile, useGetActiveProject } from '@/utils/hooks';
import { emptyMeasurement, getMeasurementsFromProduct, updateProductFromMeasurements } from '@/utils/unit-of-measurements';
import theme from './ProjectCatalogProductEditDialog.module.scss';
import { Dialog, Badge, AlertBanner, SimpleTooltip } from '@ygtechnologies/rubus-ui';

type ProjectCatalogProductEditDialogProps = {
    projectCatalogProductId: number | null;
    show: boolean;
    onClose: () => void;
    onSave: () => void;
    onCancel?: () => void;
};
const ProjectCatalogProductEditDialog: React.FC<ProjectCatalogProductEditDialogProps> = ({
    projectCatalogProductId,
    show,
    onClose,
    onSave,
    onCancel,
}) => {
    const profile = useGetActiveProfile();
    const project = useGetActiveProject();
    const [product, setProduct] = useState<OptionalId<ProjectCatalogProduct> | null>(null);
    const [measurements, setMeasurements] = useState(emptyMeasurement);
    const [saving, setSaving] = useState(false);

    if (!profile || !project) {
        return <LoadingIndicator />;
    }

    const loadData = async () => {
        if (projectCatalogProductId) {
            const p = await getProjectCatalogProduct(projectCatalogProductId);
            setProduct(p);
            setMeasurements(getMeasurementsFromProduct(p, project.defaultUnitOfMeasurement));
        } else {
            setProduct(getEmptyProduct() as ProjectCatalogProduct);
        }
    };

    function getEmptyProduct(): Partial<ProjectCatalogProduct> {
        return {
            isNone: false,
            itemCategoryId: null,
            ownerProfileId: profile!.id,
            projectId: project!.id,
            name: '',
            sku: '',
            description: '',
            productLink: '',
            images: [],
            attachments: [],
            linkGroupReferences: null,
            type: ProductType.Normal,
        };
    }

    function reset() {
        setProduct(null);
        setSaving(false);
    }

    const handleDialogOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            triggerHandler(onCancel);
            triggerHandler(onClose);
        }
    };

    const handleCancelClick = () => {
        triggerHandler(onCancel);
        triggerHandler(onClose);
    };

    const handleSaveClick = async () => {
        setSaving(true);
        const centimeterProduct = updateProductFromMeasurements(product!, measurements, project.defaultUnitOfMeasurement);
        const { images, attachments, ...cleanProduct } = centimeterProduct!;

        const files = [
            ...images.map((a) => ({ ...a, type: 'Image' }) as ProductFileUpload),
            ...attachments.map((a) => ({ ...a, type: 'Attachment' }) as ProductFileUpload),
        ];

        try {
            await saveProjectCatalogProduct(cleanProduct, files);

            triggerHandler(onSave);
            triggerHandler(onClose);
            reset();
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={show} onOpenChange={handleDialogOpenChange} onOpen={loadData}>
            <Dialog.Content contentWidth="lg">
                <Dialog.Header>
                    {projectCatalogProductId ? <Trans>Edit product</Trans> : <Trans>Create new product</Trans>}
                </Dialog.Header>

                {projectCatalogProductId &&
                    product &&
                    product.linkGroupReferences &&
                    product.linkGroupReferences.length > 0 && (
                        <AlertBanner type="warning" className={theme.warningBanner}>
                            <Trans id="project-catalog-product-edit-dialog-warning-banner-before-groups">
                                <h5>The product you are editing is already added to the following groups:</h5>
                            </Trans>
                            <div className={theme.warningBannerList}>
                                {product.linkGroupReferences.map((group) => (
                                    <SimpleTooltip key={group.id} content={group.name}>
                                        <Badge variant="warning" maxWidth={288}>
                                            {group.name}
                                        </Badge>
                                    </SimpleTooltip>
                                ))}
                            </div>
                            <Trans id="project-catalog-product-edit-dialog-warning-banner-after-groups">
                                Changing this product will be reflected in these groups.
                            </Trans>
                        </AlertBanner>
                    )}

                {product ? (
                    <EditProjectCatalogProductWizard
                        catalogProduct={product}
                        productMeasurements={measurements}
                        onProductMeasurementsChange={setMeasurements}
                        onChange={(newProduct) => setProduct(newProduct as ProjectCatalogProduct)}
                        onFinish={handleSaveClick}
                        onCancel={handleCancelClick}
                        saving={saving}
                        allowPriceEdit={true}
                        confirmDialogBody={
                            product.linkGroupReferences && product.linkGroupReferences.length > 0 ? (
                                <div>
                                    <p>
                                        <Trans>Changing this product will affect the following groups:</Trans>
                                    </p>
                                    <div className={theme.warningBannerList}>
                                        {product.linkGroupReferences.map((group) => (
                                            <SimpleTooltip key={group.id} content={group.name}>
                                                <Badge variant="warning" maxWidth={288}>
                                                    {group.name}
                                                </Badge>
                                            </SimpleTooltip>
                                        ))}
                                    </div>
                                    <p style={{ marginTop: '12px' }}>
                                        <Trans>Do you want to continue?</Trans>
                                    </p>
                                </div>
                            ) : undefined
                        }
                    />
                ) : (
                    <LoadingIndicator />
                )}
            </Dialog.Content>
        </Dialog>
    );
};

export default ProjectCatalogProductEditDialog;
